import type { DirectorySource, PreflightFinding, SourceFile } from './types.ts';

const UNSAFE_SEGMENT = /^(?:$|\.|\.\.|[\\/])|[\0]/;

export function normalizeRelativePath(path: string): string {
  if (path.startsWith('/') || path.startsWith('\\')) throw new Error(`Unsafe source path: ${path}`);
  const normalized = path.replaceAll('\\', '/').replace(/^\/+/, '');
  const parts = normalized.split('/');
  if (!normalized || parts.some((part) => UNSAFE_SEGMENT.test(part)) || /^[A-Za-z]:/.test(normalized)) {
    throw new Error(`Unsafe source path: ${path}`);
  }
  return parts.join('/');
}

export function formatBytes(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = value;
  let unit = -1;
  while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit += 1; }
  return `${size.toFixed(size >= 100 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

async function walk(handle: FileSystemDirectoryHandle, prefix = ''): Promise<SourceFile[]> {
  const files: SourceFile[] = [];
  const pending: Array<{ directory: FileSystemDirectoryHandle; path: string }> = [{ directory: handle, path: prefix }];
  // `values()` is the canonical directory enumeration path in Chromium. An
  // explicit stack avoids recursive call depth and spread-argument limits for
  // large/deep source trees.
  while (pending.length) {
    const current = pending.pop()!;
    for await (const child of current.directory.values()) {
      const name = child.name;
      const path = normalizeRelativePath(current.path ? `${current.path}/${name}` : name);
      if (child.kind === 'directory') {
        pending.push({ directory: child as FileSystemDirectoryHandle, path });
      } else {
        const fileHandle = child as FileSystemFileHandle;
        const file = await fileHandle.getFile();
        files.push({ path, name, size: file.size, lastModified: file.lastModified, type: file.type, getFile: () => fileHandle.getFile() });
      }
    }
  }
  return files;
}

export async function readDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<DirectorySource> {
  const files = await walk(handle);
  const seen = new Set<string>();
  for (const entry of files) {
    const key = entry.path.normalize('NFC').toLowerCase();
    if (seen.has(key)) throw new Error(`Duplicate normalized source path: ${entry.path}`);
    seen.add(key);
  }
  files.sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }));
  return { displayName: handle.name, files, handle };
}

export function readInputFiles(fileList: FileList | File[]): DirectorySource {
  const files: SourceFile[] = Array.from(fileList).map((file) => {
    const candidate = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    const path = normalizeRelativePath(candidate.includes('/') ? candidate.split('/').slice(1).join('/') : candidate);
    return { path, name: file.name, size: file.size, lastModified: file.lastModified, type: file.type, getFile: async () => file };
  });
  const seen = new Set<string>();
  for (const file of files) {
    const key = file.path.normalize('NFC').toLowerCase();
    if (seen.has(key)) throw new Error(`Duplicate source path: ${file.path}`);
    seen.add(key);
  }
  files.sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }));
  const first = (fileList[0] as File & { webkitRelativePath?: string } | undefined)?.webkitRelativePath;
  return { displayName: first?.split('/')[0] || 'Selected files', files };
}

export function setupCandidates(files: SourceFile[]): SourceFile[] {
  const rank = (path: string) => {
    const ext = path.toLowerCase().slice(path.lastIndexOf('.'));
    return ({ '.msi': 0, '.exe': 1, '.cmd': 2, '.bat': 3, '.ps1': 4 } as Record<string, number>)[ext] ?? 5;
  };
  return [...files].sort((a, b) => rank(a.path) - rank(b.path) || a.path.localeCompare(b.path));
}

export function sourceWarnings(source: DirectorySource): string[] {
  const warnings: string[] = [];
  if (!source.files.length) warnings.push('The selected source contains no files.');
  const total = source.files.reduce((sum, file) => sum + file.size, 0);
  if (total >= 25 * 1024 ** 3) warnings.push(`Source is ${formatBytes(total)}; Intune documents a 30 GB app limit.`);
  if (source.files.some((file) => file.path.toLowerCase().endsWith('.intunewin'))) warnings.push('The source contains an existing .intunewin file; remove it unless it is intentional.');
  if (source.files.some((file) => /IntuneWinAppUtil\.exe$/i.test(file.path))) warnings.push('The Microsoft prep tool is inside the source tree and would be packaged.');
  if (source.files.some((file) => file.path.split('/').length > 30)) warnings.push('One or more paths are unusually deep and may not work on Windows.');
  return warnings;
}

/** Run actionable checks before the user spends time creating a package. */
export function sourcePreflight(source: DirectorySource, setupFile = ''): PreflightFinding[] {
  const findings: PreflightFinding[] = [];
  if (!source.files.length) findings.push({ severity: 'error', message: 'The selected source contains no files.' });
  const total = source.files.reduce((sum, file) => sum + file.size, 0);
  if (total >= 30 * 1024 ** 3) findings.push({ severity: 'error', message: `Source is ${formatBytes(total)}; this exceeds Intune's documented 30 GB app limit.` });
  else if (total >= 25 * 1024 ** 3) findings.push({ severity: 'warning', message: `Source is ${formatBytes(total)}; it is close to Intune's documented 30 GB app limit.` });
  if (setupFile && !source.files.some((file) => file.path === setupFile)) findings.push({ severity: 'error', message: 'The selected setup file is not present in the source inventory.' });
  if (source.files.some((file) => file.path.toLowerCase().endsWith('.intunewin'))) findings.push({ severity: 'warning', message: 'The source contains an existing .intunewin file; remove it unless it is intentional.' });
  if (source.files.some((file) => /IntuneWinAppUtil\.exe$/i.test(file.path))) findings.push({ severity: 'warning', message: 'The Microsoft prep tool is inside the source tree and would be packaged.' });
  const deep = source.files.filter((file) => file.path.split('/').length > 30 || file.path.length > 240);
  if (deep.length) findings.push({ severity: 'warning', message: `${deep.length} path${deep.length === 1 ? '' : 's'} may be too deep or long for Windows deployment.` });
  if (source.files.length > 10000) findings.push({ severity: 'info', message: `${source.files.length.toLocaleString()} files detected; inventory and packaging may take longer.` });
  const largest = Math.max(0, ...source.files.map((file) => file.size));
  if (largest >= 4 * 1024 ** 3) findings.push({ severity: 'info', message: `Largest file is ${formatBytes(largest)}; the streaming path will be used when supported.` });
  return findings;
}
